import type { ApiError, AuthTokens, CurrentUser } from '@performance-crm/shared';

const API_URL = import.meta.env.VITE_API_URL ?? '';

let accessToken: string | null = null;
let refreshing: Promise<boolean> | null = null;
const listeners = new Set<(user: CurrentUser | null) => void>();
let currentUser: CurrentUser | null = null;

export class ApiRequestError extends Error {
  constructor(
    public readonly status: number,
    public readonly body: ApiError,
  ) {
    super(body.message);
  }
}

function setSession(tokens: AuthTokens | null) {
  accessToken = tokens?.accessToken ?? null;
  currentUser = tokens?.user ?? null;
  for (const l of listeners) l(currentUser);
}

export function onSessionChange(listener: (user: CurrentUser | null) => void) {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

/** URL absoluta de una ruta de la API (enlaces firmados de adjuntos que se abren fuera del cliente). */
export function apiUrl(path: string): string {
  return `${API_URL}${path}`;
}

export function getCurrentUser() {
  return currentUser;
}

async function rawFetch(path: string, init: RequestInit = {}, withAuth = true): Promise<Response> {
  const headers = new Headers(init.headers);
  // Un FormData (adjuntos) deja que el navegador ponga el multipart con su boundary.
  if (init.body && !(init.body instanceof FormData) && !headers.has('content-type'))
    headers.set('content-type', 'application/json');
  if (withAuth && accessToken) headers.set('authorization', `Bearer ${accessToken}`);
  return fetch(`${API_URL}${path}`, { ...init, headers, credentials: 'include' });
}

/** Intenta renovar la sesión con la cookie de refresh. Devuelve true si hay sesión. */
export async function refreshSession(): Promise<boolean> {
  if (!refreshing) {
    refreshing = (async () => {
      try {
        const res = await rawFetch('/api/v1/auth/refresh', { method: 'POST' }, false);
        if (!res.ok) {
          setSession(null);
          return false;
        }
        setSession((await res.json()) as AuthTokens);
        return true;
      } catch {
        setSession(null);
        return false;
      } finally {
        refreshing = null;
      }
    })();
  }
  return refreshing;
}

export async function api<T>(path: string, init: RequestInit = {}): Promise<T> {
  let res = await rawFetch(path, init);
  if (res.status === 401 && accessToken !== null) {
    const ok = await refreshSession();
    if (ok) res = await rawFetch(path, init);
  }
  if (!res.ok) {
    let body: ApiError = { statusCode: res.status, error: 'ERROR', message: 'Error de red' };
    try {
      body = (await res.json()) as ApiError;
    } catch {
      /* respuesta sin cuerpo */
    }
    throw new ApiRequestError(res.status, body);
  }
  return (await res.json()) as T;
}

export async function login(username: string, password: string): Promise<CurrentUser> {
  const res = await rawFetch(
    '/api/v1/auth/login',
    { method: 'POST', body: JSON.stringify({ username, password }) },
    false,
  );
  if (!res.ok) {
    const body = (await res.json().catch(() => ({
      statusCode: res.status,
      error: 'ERROR',
      message: 'No se pudo iniciar sesión',
    }))) as ApiError;
    throw new ApiRequestError(res.status, body);
  }
  const tokens = (await res.json()) as AuthTokens;
  setSession(tokens);
  return tokens.user;
}

export async function logout(): Promise<void> {
  await rawFetch('/api/v1/auth/logout', { method: 'POST' }).catch(() => undefined);
  setSession(null);
}

/** Descarga binaria autenticada (exportaciones). */
/** Descarga un archivo con sesión; devuelve también las cabeceras (p. ej. si la exportación se truncó). */
export async function apiDownload(path: string): Promise<{ blob: Blob; headers: Headers }> {
  let res = await rawFetch(path);
  if (res.status === 401 && accessToken !== null && (await refreshSession()))
    res = await rawFetch(path);
  if (!res.ok) {
    let message = 'No se pudo descargar el archivo';
    try {
      const body = (await res.json()) as { message?: string };
      if (body?.message) message = body.message;
    } catch {
      // sin cuerpo JSON: mensaje genérico
    }
    throw new ApiRequestError(res.status, { statusCode: res.status, error: 'ERROR', message });
  }
  return { blob: await res.blob(), headers: res.headers };
}

export async function apiBlob(path: string): Promise<Blob> {
  return (await apiDownload(path)).blob;
}
